/**
 * Which tabs the admin panel has, and the order they were built in.
 *
 * This file is the single source of truth for the tab list. The saved arrangement in
 * platform_settings only records the order of these values, so a tab added here later shows up
 * at the end of its category without anyone having to re-save anything.
 */
import { Palette, Settings, Wrench, BarChart3 } from 'lucide-react';

export const TAB_CATEGORIES = [
  {
    key: 'creation',
    label: 'Creation',
    icon: Palette,
    color: 'bg-indigo-900/40 text-indigo-200',
    tabs: [
      { value: 'modules', label: 'Modules' },
      { value: 'folders', label: 'Folders' },
      { value: 'pacing', label: 'Pacing' },
      { value: 'practitioners', label: 'Practitioners' },
      { value: 'vendor-invites', label: 'Vendors' },
      { value: 'onboarding', label: 'Onboarding' },
      { value: 'content', label: 'Content' },
      { value: 'worksheets', label: 'Worksheets' },
      { value: 'ritual-cards', label: 'Ritual Cards' },
      { value: 'assessment-builder', label: 'Assessment Builder' },
      { value: 'assessment-scoring', label: 'Assessment Scoring' },
      { value: 'forum', label: 'Forum' },
      { value: 'calendar', label: 'Calendar' },
      { value: 'event-approvals', label: 'Events & Polls', attentionKey: 'event_approvals' },
      { value: 'resources', label: 'Resources/Guides' },
      { value: 'streams', label: 'Streams' },
      { value: 'music', label: 'Music' },
      { value: 'portal-videos', label: 'Portal Videos' },
      { value: 'tutorials', label: 'Tutorials' },
      { value: 'spotlights', label: 'Advertising', attentionKey: 'spotlight_promotions' },
    ],
  },
  {
    key: 'management',
    label: 'Management',
    icon: Settings,
    color: 'bg-emerald-900/40 text-emerald-200',
    tabs: [
      { value: 'messages', label: 'Messages', attentionKey: 'messages' },
      { value: 'members', label: 'Members' },
      { value: 'blocked', label: 'Blocked' },
      { value: 'pending', label: 'Pending', attentionKey: 'pending' },
      { value: 'contributors', label: 'Contributors' },
      { value: 'workshops', label: 'Workshops', attentionKey: 'workshops' },
      { value: 'mkt-vendors', label: 'Mkt Vendors', attentionKey: 'marketplace_vendors' },
      { value: 'shelves', label: 'Mkt Shelves' },
      { value: 'mkt-inventory', label: 'Mkt Inventory' },
      { value: 'qa', label: 'Q&A' },
      { value: 'mkt-orders', label: 'Mkt Orders', attentionKey: 'return_escalations' },
      { value: 'mkt-payouts', label: 'Mkt Payouts' },
      { value: 'emails', label: 'Emails' },
      { value: 'account-access', label: 'Account Access' },
      { value: 'reports', label: 'Reports', attentionKey: 'reports' },
    ],
  },
  {
    key: 'backend',
    label: 'Back End',
    icon: Wrench,
    color: 'bg-amber-900/40 text-amber-200',
    tabs: [
      { value: 'tier-pricing', label: 'Tier Pricing' },
      { value: 'promotions', label: 'Promotions' },
      { value: 'subscription-pricing', label: 'Subscription Pricing' },
      { value: 'payouts', label: 'Payouts' },
      { value: 'sales-tax', label: 'Sales Tax' },
      { value: 'stripe', label: 'Stripe' },
      { value: 'launch', label: 'Launch' },
      { value: 'legal', label: 'Legal Docs' },
    ],
  },
  {
    key: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    color: 'bg-purple-900/40 text-purple-200',
    tabs: [
      { value: 'analytics', label: 'Platform Analytics' },
    ],
  },
];

/**
 * The tabs of every category, arranged by a saved order.
 *
 * A saved value is honoured wherever it was put, so a tab dragged into another group stays
 * there. Anything saved that no longer exists is dropped; anything that exists but was never
 * saved — a tab added after the arrangement was last touched — goes to the end of the category
 * it was built in. Returns [{ ...category, tabs: [...] }].
 */
export const arrangeTabs = (saved) => {
  const byValue = new Map();
  TAB_CATEGORIES.forEach((c) => c.tabs.forEach((t) => byValue.set(t.value, t)));

  const placed = new Set();
  const arranged = TAB_CATEGORIES.map((category) => {
    const tabs = [];
    ((saved && saved[category.key]) || []).forEach((value) => {
      const tab = byValue.get(value);
      if (tab && !placed.has(value)) {
        placed.add(value);
        tabs.push(tab);
      }
    });
    return { ...category, tabs };
  });

  // Whatever was never mentioned goes to the end of its home category.
  TAB_CATEGORIES.forEach((category, i) => {
    category.tabs.forEach((tab) => {
      if (!placed.has(tab.value)) {
        placed.add(tab.value);
        arranged[i].tabs.push(tab);
      }
    });
  });

  return arranged;
};
